// Zod schemas (and inferred types) for the onboarding policy: jurisdiction
// profiles, flow stages, and the policy envelope. Parsing the policy at load
// time gives fail-fast validation of hand-authored or DB-sourced config.

import { z } from 'zod';

import { flowConditionSchema } from './conditions.js';

export const onboardingDocumentTypeSchema = z.enum(['PASSPORT', 'NATIONAL_ID', 'DRIVING_LICENCE']);
export type OnboardingDocumentType = z.infer<typeof onboardingDocumentTypeSchema>;

export const consentSchema = z.object({
  id: z.string(),
  required: z.boolean(),
  version: z.string(),
  label: z.string(),
});
export type ConsentDefinition = z.infer<typeof consentSchema>;

export const addressFieldSchema = z.object({
  id: z.string(),
  label: z.string(),
  required: z.boolean(),
  /** Regex source string for client-side validation, if any. */
  pattern: z.string().optional(),
  placeholder: z.string().optional(),
});
export type AddressFieldDefinition = z.infer<typeof addressFieldSchema>;

export const blockedSubregionSchema = z.object({
  code: z.string(),
  label: z.string(),
  reason: z.string(),
});
export type BlockedSubregion = z.infer<typeof blockedSubregionSchema>;

export const jurisdictionProfileSchema = z.object({
  id: z.string(),
  displayName: z.string(),
  locale: z.string(),
  currency: z.string(), // ISO 4217
  accountIdentifier: z.enum(['IBAN', 'ACCOUNT_NUMBER']),
  amlRegulation: z.object({
    actName: z.string(),
    retentionYears: z.number().int().nonnegative(),
  }),
  privacyRegulation: z.object({ actName: z.string() }),
  phone: z.object({ defaultCountry: z.string(), defaultDialCode: z.string() }),
  identityDocuments: z.array(onboardingDocumentTypeSchema).min(1),
  address: z.object({
    countryDefault: z.string(),
    countryOptions: z.array(z.object({ code: z.string(), name: z.string() })),
    fields: z.array(addressFieldSchema),
  }),
  consents: z.array(consentSchema),
  blockedSubregions: z.array(blockedSubregionSchema),
});
export type JurisdictionProfile = z.infer<typeof jurisdictionProfileSchema>;

export const onboardingPhaseSchema = z.enum(['account', 'identity', 'welcome']);

export const flowStageSchema = z.object({
  id: z.string(),
  /** The client screen that renders this stage. */
  screen: z.string(),
  phase: onboardingPhaseSchema,
  label: z.string(),
  /** Stage is included only when this condition matches the criteria. Omit = always. */
  appliesWhen: flowConditionSchema.optional(),
});
export type FlowStage = z.infer<typeof flowStageSchema>;

export const onboardingPolicySchema = z.object({
  version: z.string(),
  defaultJurisdiction: z.string(),
  /** Map of ISO-3166 alpha-2 country → jurisdiction id. */
  countryJurisdiction: z.record(z.string(), z.string()),
  jurisdictions: z.array(jurisdictionProfileSchema).min(1),
  /** Ordered superset of stages; the resolver filters by `appliesWhen`. */
  stages: z.array(flowStageSchema).min(1),
});
export type OnboardingPolicy = z.infer<typeof onboardingPolicySchema>;
